import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { productionFileSyncService, PendingFileSync, FileSyncState, CompletedFileSync } from '../services/ProductionFileSyncService';
import { useAuth } from '../contexts/AuthContext';

type TabType = 'queue' | 'completed' | 'failed';

const FileSyncStatusIcon: React.FC = () => {
  const { accessLevel } = useAuth();
  if (accessLevel !== 'FULL_ACCESS') return null;
  const [syncStatus, setSyncStatus] = useState<{
    pending: PendingFileSync[];
    active: FileSyncState[];
    completed: CompletedFileSync[];
    failed: CompletedFileSync[];
    totalSuccessfullyProcessed: number;
  }>({ pending: [], active: [], completed: [], failed: [], totalSuccessfullyProcessed: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('queue');
  const [isLoading, setIsLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const shouldPollFastRef = useRef<boolean>(false);

  // Fetch sync status
  const fetchSyncStatus = async () => {
    try {
      setIsLoading(true);
      const status = await productionFileSyncService.getSyncStatus();
      setSyncStatus({
        pending: status.pending || [],
        active: status.active || [],
        completed: status.completed || [],
        failed: status.failed || [],
        totalSuccessfullyProcessed: status.totalSuccessfullyProcessed || 0,
      });
    } catch (error) {
      console.error('[FileSyncStatusIcon] Error fetching sync status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Dynamic polling: 10 seconds when pending/active, 1 minute when idle
  const setupPolling = useCallback(() => {
    const hasPendingOrActive = syncStatus.pending.length > 0 || syncStatus.active.length > 0;
    const shouldPollFast = hasPendingOrActive || shouldPollFastRef.current;
    const pollInterval = shouldPollFast ? 10000 : 60000; // 10 seconds if active, 1 minute if idle
    
    // If queue is empty, reset the trigger flag
    if (!hasPendingOrActive) {
      shouldPollFastRef.current = false;
    }
    
    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Set up new interval
    intervalRef.current = setInterval(() => {
      fetchSyncStatus();
    }, pollInterval);
  }, [syncStatus.pending.length, syncStatus.active.length]);

  useEffect(() => {
    // Initial fetch
    fetchSyncStatus();
    
    // Set up polling based on current state
    setupPolling();
    
    // Listen for sync button clicks to switch to fast polling
    const handleSyncTriggered = () => {
      shouldPollFastRef.current = true;
      setupPolling(); // Immediately switch to fast polling
    };
    
    window.addEventListener('syncTriggered', handleSyncTriggered);
    
    return () => {
      window.removeEventListener('syncTriggered', handleSyncTriggered);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [setupPolling]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isModalOpen]);

  // Determine icon color
  const getIconColor = (): string => {
    const hasFailed = syncStatus.failed.length > 0;
    if (hasFailed) {
      return '#ef4444'; // Red for failed
    }
    const hasPending = syncStatus.pending.length > 0;
    const hasActive = syncStatus.active.length > 0;
    if (hasPending || hasActive) {
      return '#3b82f6'; // Blue for active/pending
    }
    return '#9ca3af'; // Gray for idle
  };

  // Get icon emoji
  const getIcon = (): string => {
    return '⬆️'; // Upload icon
  };

  // Get total counts (recalculate on every render to ensure they're up to date)
  const totalPending = syncStatus.pending?.length || 0;
  const totalActive = syncStatus.active?.length || 0;
  const totalFailed = syncStatus.failed?.length || 0;
  const totalCompleted = syncStatus.completed?.length || 0;

  // Format date
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  // Calculate progress percentage
  const getProgressPercent = (sync: FileSyncState): number => {
    if (sync.progress.filesTotal === 0) return 0;
    const synced = sync.progress.filesSynced + sync.progress.filesDeleted;
    return Math.round((synced / sync.progress.filesTotal) * 100);
  };

  return (
    <>
      <div
        key={`sync-status-${totalPending}-${totalActive}-${totalFailed}-${syncStatus.totalSuccessfullyProcessed || 0}`}
        onClick={() => setIsModalOpen(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px',
          cursor: 'pointer',
          fontSize: '20px',
          position: 'relative',
          transition: 'transform 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
        className="sync-status-icon"
      >
        <span style={{ color: getIconColor(), filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}>
          {getIcon()}
        </span>
        {(totalPending > 0 || totalActive > 0 || totalFailed > 0) && (
          <span
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              backgroundColor: totalFailed > 0 ? '#ef4444' : '#3b82f6',
              color: 'white',
              borderRadius: '50%',
              minWidth: '26px',
              minHeight: '26px',
              padding: '0 6px',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              border: '2px solid var(--bg-primary)',
              boxSizing: 'border-box',
            }}
          >
            {totalPending + totalActive + totalFailed}
          </span>
        )}
        <div className="help-tooltip" style={{ minWidth: '280px', maxWidth: '400px' }}>
          <div className="tooltip-content">
            <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              Sync Queue Status
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '60%' }} />
                <col style={{ width: '40%' }} />
              </colgroup>
              <tbody>
                <tr>
                  <td style={{ padding: '0.25rem 0.5rem 0.25rem 0', color: 'var(--text-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>Pending:</td>
                  <td style={{ padding: '0.25rem 0 0.25rem 0.5rem', color: 'var(--text-primary)', fontWeight: 500, whiteSpace: 'nowrap' }}>{totalPending}</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.25rem 0.5rem 0.25rem 0', color: 'var(--text-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>Active:</td>
                  <td style={{ padding: '0.25rem 0 0.25rem 0.5rem', color: 'var(--text-primary)', fontWeight: 500, whiteSpace: 'nowrap' }}>{totalActive}</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.25rem 0.5rem 0.25rem 0', color: 'var(--text-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>Failed:</td>
                  <td style={{ padding: '0.25rem 0 0.25rem 0.5rem', color: totalFailed > 0 ? '#ef4444' : 'var(--text-primary)', fontWeight: 500, whiteSpace: 'nowrap' }}>{totalFailed}</td>
                </tr>
                <tr>
                  <td style={{ padding: '0.25rem 0.5rem 0.25rem 0', color: 'var(--text-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>Completed:</td>
                  <td style={{ padding: '0.25rem 0 0.25rem 0.5rem', color: 'var(--accent-success)', fontWeight: 500, whiteSpace: 'nowrap' }}>{syncStatus.totalSuccessfullyProcessed || 0}</td>
                </tr>
              </tbody>
            </table>
            {(totalPending > 0 || totalActive > 0) && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                Updated: {new Date().toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: '5px',
            zIndex: 99999,
            overflowY: 'auto',
          }}
          onClick={() => setIsModalOpen(false)}
        >
          <div
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-primary)',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '1000px',
              maxHeight: 'calc(100vh - 10px)',
              marginTop: '5px',
              marginBottom: '5px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              position: 'relative',
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '1rem',
                borderBottom: '1px solid var(--border-primary)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <h2 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--text-primary)' }}>
                Production File Sync Queue
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.75rem',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                  padding: '0.25rem 0.5rem',
                  lineHeight: 1,
                  fontWeight: 'bold',
                  minWidth: '32px',
                  minHeight: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--text-primary)';
                  e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--text-secondary)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                title="Close"
              >
                ×
              </button>
            </div>

            {/* Tabs */}
            <div
              style={{
                display: 'flex',
                borderBottom: '1px solid var(--border-primary)',
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => setActiveTab('queue')}
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  background: activeTab === 'queue' ? 'var(--bg-primary)' : 'transparent',
                  border: 'none',
                  borderBottom: activeTab === 'queue' ? '2px solid var(--accent-primary)' : '2px solid transparent',
                  color: activeTab === 'queue' ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'queue' ? 600 : 400,
                  fontSize: '0.875rem',
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'queue') {
                    e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'queue') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Queue ({totalPending + totalActive})
              </button>
              <button
                onClick={() => setActiveTab('completed')}
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  background: activeTab === 'completed' ? 'var(--bg-primary)' : 'transparent',
                  border: 'none',
                  borderBottom: activeTab === 'completed' ? '2px solid var(--accent-success)' : '2px solid transparent',
                  color: activeTab === 'completed' ? 'var(--accent-success)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'completed' ? 600 : 400,
                  fontSize: '0.875rem',
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'completed') {
                    e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'completed') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Complete (Success) ({totalCompleted})
              </button>
              <button
                onClick={() => setActiveTab('failed')}
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  background: activeTab === 'failed' ? 'var(--bg-primary)' : 'transparent',
                  border: 'none',
                  borderBottom: activeTab === 'failed' ? '2px solid #ef4444' : '2px solid transparent',
                  color: activeTab === 'failed' ? '#ef4444' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'failed' ? 600 : 400,
                  fontSize: '0.875rem',
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'failed') {
                    e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'failed') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Complete (Failed) ({totalFailed})
              </button>
            </div>

            {/* Content - Scrollable Table */}
            <div
              style={{
                overflowY: 'auto',
                flex: 1,
                padding: '1rem',
              }}
            >
              {isLoading && (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                  Loading...
                </div>
              )}

              {!isLoading && activeTab === 'queue' && (
                <>
                  {totalPending === 0 && totalActive === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      No syncs in queue
                    </div>
                  ) : (
                    <>
                      {/* Pending Queue */}
                      {totalPending > 0 && (
                        <div style={{ marginBottom: '2rem' }}>
                          <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', color: 'var(--text-primary)' }}>
                            Pending ({totalPending})
                          </h3>
                          <div
                            style={{
                              border: '1px solid var(--border-primary)',
                              borderRadius: '4px',
                              overflow: 'hidden',
                            }}
                          >
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                              <thead>
                                <tr style={{ backgroundColor: 'var(--bg-secondary)' }}>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', fontWeight: 600 }}>ISBN</th>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', fontWeight: 600 }}>User</th>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', fontWeight: 600 }}>Folder</th>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', fontWeight: 600 }}>Queued At</th>
                                </tr>
                              </thead>
                              <tbody>
                                {syncStatus.pending.map((item, idx) => (
                                  <tr key={`${item.isbn}-${idx}`} style={{ backgroundColor: idx % 2 === 0 ? 'var(--bg-primary)' : 'var(--bg-secondary)' }}>
                                    <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>{item.isbn}</td>
                                    <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>{item.username}</td>
                                    <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>{item.folder}</td>
                                    <td style={{ padding: '0.75rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{formatDate(item.queuedAt)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Active Syncs */}
                      {totalActive > 0 && (
                        <div>
                          <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', color: 'var(--text-primary)' }}>
                            Active ({totalActive})
                          </h3>
                          <div style={{ border: '1px solid var(--border-primary)', borderRadius: '4px', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                              <thead>
                                <tr style={{ backgroundColor: 'var(--bg-secondary)' }}>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', fontWeight: 600 }}>ISBN</th>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', fontWeight: 600 }}>Progress</th>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', fontWeight: 600 }}>Operation</th>
                                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', fontWeight: 600 }}>Started</th>
                                </tr>
                              </thead>
                              <tbody>
                                {syncStatus.active.map((sync, idx) => {
                                  const progress = getProgressPercent(sync);
                                  return (
                                    <tr key={`${sync.isbn}-${idx}`} style={{ backgroundColor: idx % 2 === 0 ? 'var(--bg-primary)' : 'var(--bg-secondary)' }}>
                                      <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>{sync.isbn}</td>
                                      <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                          <div style={{ flex: 1, height: '8px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '4px', overflow: 'hidden' }}>
                                            <div style={{ width: `${progress}%`, height: '100%', backgroundColor: '#3b82f6', transition: 'width 0.3s ease' }} />
                                          </div>
                                          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', minWidth: '45px' }}>{progress}%</span>
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                                          {sync.progress.filesSynced} synced, {sync.progress.filesDeleted} deleted / {sync.progress.filesTotal} total
                                        </div>
                                      </td>
                                      <td style={{ padding: '0.75rem', color: 'var(--text-primary)', fontSize: '0.8rem' }}>{sync.progress.currentOperation}</td>
                                      <td style={{ padding: '0.75rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{formatDate(sync.startedAt)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {!isLoading && activeTab === 'completed' && (
                <>
                  {totalCompleted === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      No completed syncs
                    </div>
                  ) : (
                    <div style={{ border: '1px solid var(--border-primary)', borderRadius: '4px', overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                        <thead>
                          <tr style={{ backgroundColor: 'var(--bg-secondary)' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', fontWeight: 600 }}>ISBN</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', fontWeight: 600 }}>User</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', fontWeight: 600 }}>Folder</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', fontWeight: 600 }}>Files</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', fontWeight: 600 }}>Synced To</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid var(--border-primary)', color: 'var(--text-primary)', fontWeight: 600 }}>Completed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {syncStatus.completed.map((item, idx) => (
                            <tr key={`${item.isbn}-${idx}`} style={{ backgroundColor: idx % 2 === 0 ? 'var(--bg-primary)' : 'var(--bg-secondary)' }}>
                              <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>{item.isbn}</td>
                              <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>{item.username}</td>
                              <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>{item.folder}</td>
                              <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>
                                {item.filesSynced} synced, {item.filesDeleted} deleted / {item.filesTotal} total
                              </td>
                              <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>{item.syncedTo?.join(', ') || 'N/A'}</td>
                              <td style={{ padding: '0.75rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{formatDate(item.completedAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {!isLoading && activeTab === 'failed' && (
                <>
                  {totalFailed === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      No failed syncs
                    </div>
                  ) : (
                    <div style={{ border: '1px solid #ef4444', borderRadius: '4px', overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                        <thead>
                          <tr style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ef4444', color: '#ef4444', fontWeight: 600 }}>ISBN</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ef4444', color: '#ef4444', fontWeight: 600 }}>User</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ef4444', color: '#ef4444', fontWeight: 600 }}>Folder</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ef4444', color: '#ef4444', fontWeight: 600 }}>Error</th>
                            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ef4444', color: '#ef4444', fontWeight: 600 }}>Failed At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {syncStatus.failed.map((item, idx) => (
                            <tr key={`${item.isbn}-${idx}`} style={{ backgroundColor: idx % 2 === 0 ? 'var(--bg-primary)' : 'var(--bg-secondary)' }}>
                              <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>{item.isbn}</td>
                              <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>{item.username}</td>
                              <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>{item.folder}</td>
                              <td style={{ padding: '0.75rem', color: '#ef4444', fontSize: '0.8rem', maxWidth: '300px', wordBreak: 'break-word' }} title={item.error}>
                                {item.error || 'Unknown error'}
                              </td>
                              <td style={{ padding: '0.75rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{formatDate(item.completedAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        , document.body
      )}
    </>
  );
};

export default FileSyncStatusIcon;

